(ns ui.editors.open-search.view
  (:require [common.entity :as entity]
            [common.opensearch.entity-indices :as entity-indices]
            [common.opensearch.fields :as cos]
            [common.schema.field :as field]
            [common.schema.entities.pipeline :as pipeline]
            [common.schema.entity-types :as entity-types]
            [common.util :as cu]
            [frontend.conn :as conn]
            [frontend.state.current-session :as session]
            [promesa.core :as p]
            [ui.components.select.async-multi-select :as async-multi-select]
            [ui.components.select.async-select :as async-select]
            [ui.editors.open-search.model :as m]))

(defn open-search!
  [{:keys [containing-entity creation-pipeline-id named-value-attribute field/os-filter-terms-fn
           search-query success-callback! entity-types start-index page-size] :as ctx}]
  (let [pipeline-id (or (pipeline/containing-pipeline-id containing-entity)
                        creation-pipeline-id)]
    (-> (conn/sendp! :search/universal-search
                     {:query        search-query
                      :entity-types (vec entity-types)
                      :sort-terms   (if named-value-attribute
                                      [{"original.common/ord" {:order "asc"}}]
                                      [{"extras.lower-case-display-name.keyword" {:order "asc"}}])
                      :filter-terms (m/filter-terms
                                     {:entity-types          entity-types
                                      :named-value-attribute named-value-attribute
                                      :pipeline-id           pipeline-id
                                      :field-filter-terms    (when (fn? os-filter-terms-fn)
                                                               (os-filter-terms-fn ctx))})})
        (p/then
         (fn [{:keys [body]}]
           (let [response (-> body
                              :responses
                              first)
                 total-hit-count (-> response
                                     :hits
                                     :total
                                     :value)
                 hits (-> response
                          :hits
                          :hits)
                 has-next-page? (and (number? page-size)
                                     (number? start-index)
                                     (= (count hits) page-size)
                                     (< (+ start-index page-size) total-hit-count))
                 options (->> hits
                              (map (fn [hit]
                                     {:value (-> hit :_id (js/parseInt))
                                      :icon  (when (> (count entity-types) 1)
                                               (-> hit :_source :original :entity/type
                                                   (cu/str->keyword-if-string)
                                                   (entity-types/resolve-icon)))
                                      :label (str                                                   ;_(or (-> hit :_score) "0")
                                              ;" "
                                              (cos/hit->entity-name hit))})))
                 page {:options        options
                       :has-next-page? (boolean has-next-page?)}]
             (when (fn? success-callback!)
               (success-callback! options))
             page))))))

(defn- filter-local-entities
  [{:keys [containing-entity field/option-filter-fn search-query entity-types-lookup]}
   entities]
  (if-not (fn? option-filter-fn)
    entities
    (let [search-str-lc (cu/str-trim-lcase search-query)]
      (filterv (fn [entity-candidate]
                 (option-filter-fn
                  {:search-str-lc         search-str-lc
                   :containing-entity     containing-entity
                   :has-feature-access-fn session/has-feature-access?
                   :has-focus?            true
                   :search-fn?            identity
                   :entity-candidate      entity-candidate
                   :entity-types-lookup   entity-types-lookup}))
               entities))))

(defn local-search
  [{:keys [search-query entity-types success-callback!] :as args}]
  (let [entities (->> (session/local-search (assoc args :entity-types entity-types
                                                        :search-query search-query))
                      (filter-local-entities
                       (assoc args :entity-types-lookup @session/entity-types)))
        options (->> entities
                     (mapv (fn [e]
                             {:value (:db/id e)
                              :label (entity/display-string e)})))
        page {:options        options
              :has-next-page? false}]
    (when (fn? success-callback!)
      (success-callback! options))
    page))

(defn get-options
  [{:keys [entity-types] :as args}]
  (if (every? entity-indices/is-search-indexed? entity-types)
    (open-search! args)
    (local-search args)))

(defn multi-value
  [{:keys [_value _on-change _on-menu-open! _on-menu-close! _creation-pipeline-id]}]
  (let [id-str (gensym "opensearch_m_")]
    (fn [{:keys [value on-change on-menu-open! on-menu-close! disabled? create! placeholder] :as args}]
      (assert (or (nil? value)
                  (every? (fn [opt]
                            (and (:label opt)
                                 (:value opt)))
                          value))
              (str "Open search value must be [{:label :value} ..] list " (pr-str value)))
      (let [entity-types (field/select-entity-types args)
            create-entity-types (field/create-entity-types args)
            creatable? (and (seq create-entity-types)
                            (fn? create!))]
        ^{:key id-str}
        [async-multi-select/view
         {:value               value
          :on-change           on-change
          :max-hits-per-page   50
          :get-options         (fn [input callback]
                                 (get-options (assoc args
                                                     :entity-types entity-types
                                                     :search-query input
                                                     :success-callback! callback)))
          :on-menu-open!       on-menu-open!
          :on-menu-close!      on-menu-close!
          :auto-focus?         false
          :is-clearable?       (-> args :field/not-clearable? (not))
          :is-multi?           true
          :open-menu-on-focus? true
          :disabled?           disabled?
          :create!             (when creatable?
                                 create!)
          :placeholder         placeholder}]))))

(defn single-value
  [{:keys [_on-change _value _exclude-values _placeholder _on-menu-open! _on-menu-close! _create! _creation-pipeline-id _auto-focus?]}]
  (let [id-str (gensym "opensearch_s_")]
    (fn [{:keys [on-change value exclude-values on-menu-open! on-menu-close! auto-focus? create! placeholder disabled?] :as args}]
      (assert (or (nil? value)
                  (and (:label value)
                       (:value value)))
              (str "Open search value must be {:value :label} map " (pr-str value)))
      (let [entity-types (field/select-entity-types args)
            create-entity-types (field/create-entity-types args)
            creatable? (and (seq create-entity-types)
                            (fn? create!))]
        ^{:key id-str}
        [async-select/view
         {:value               value
          :on-change           on-change
          :on-menu-open!       on-menu-open!
          :on-menu-close!      on-menu-close!
          :placeholder         placeholder
          :max-hits-per-page   50
          :get-options         (fn [input callback]
                                 (get-options (assoc args
                                                     :entity-types entity-types
                                                     :search-query input
                                                     :success-callback! (fn [options]
                                                                          (callback (->> options
                                                                                         (remove (fn [{:keys [value]}]
                                                                                                   (some #{value} exclude-values)))))))))
          :auto-focus?         auto-focus?
          :is-clearable?       (-> args :field/not-clearable? (not))
          :is-multi?           false
          :open-menu-on-focus? true
          :create!             (when creatable?
                                 create!)
          :disabled?           disabled?}]))))
